import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Calendar, Clock, Users, Radio, Bell, BellOff, ArrowLeft, Loader2,
  Trash2, Share2
} from 'lucide-react';
import UserAvatar from '../components/UserAvatar';

const CATEGORIES = {
  general: { label: 'General', color: 'bg-muted text-foreground' },
  music: { label: 'Music Session', color: 'bg-violet-500/20 text-violet-400' },
  podcast: { label: 'Podcast', color: 'bg-blue-500/20 text-blue-400' },
  battle: { label: 'Battle', color: 'bg-red-500/20 text-red-400' },
  collab: { label: 'Collab', color: 'bg-green-500/20 text-green-400' },
  qa: { label: 'Q&A', color: 'bg-amber-500/20 text-amber-400' },
  listening_party: { label: 'Listening Party', color: 'bg-pink-500/20 text-pink-400' },
  interview: { label: 'Interview', color: 'bg-cyan-500/20 text-cyan-400' },
};

function formatCountdown(scheduledAt) {
  const diff = new Date(scheduledAt) - new Date();
  if (diff <= 0) return 'Starting now!';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins} minutes`;
}

export default function EventDetailPage() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/scheduled-events/${eventId}`);
        if (res.ok) {
          const data = await res.json();
          setEvent(data);
          setCountdown(formatCountdown(data.scheduled_at));
        } else {
          toast.error('Event not found');
          navigate('/events');
        }
      } catch {
        toast.error('Failed to load event');
      }
      try {
        const res = await fetch(`${API}/scheduled-events/${eventId}/rsvps`);
        if (res.ok) setRsvps((await res.json()).rsvps || []);
      } catch { /* */ }
      setLoading(false);
    };
    fetchData();
  }, [eventId, navigate]);

  useEffect(() => {
    if (!event) return;
    const interval = setInterval(() => setCountdown(formatCountdown(event.scheduled_at)), 30000);
    return () => clearInterval(interval);
  }, [event]);

  const handleRsvp = async () => {
    if (!user) { toast.error('Please log in'); return; }
    try {
      const res = await fetch(`${API}/scheduled-events/${eventId}/rsvp`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        const r = await fetch(`${API}/scheduled-events/${eventId}`);
        if (r.ok) setEvent(await r.json());
        const rr = await fetch(`${API}/scheduled-events/${eventId}/rsvps`);
        if (rr.ok) setRsvps((await rr.json()).rsvps || []);
      }
    } catch { toast.error('Network error'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Cancel this event? All RSVPs will be notified.')) return;
    try {
      const res = await fetch(`${API}/scheduled-events/${eventId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (res.ok) { toast.success('Event cancelled'); navigate('/events'); }
    } catch { toast.error('Failed to cancel'); }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/events/${eventId}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied!')).catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) return null;

  const isHost = user?.user_id === event.host_user_id;
  const isRsvpd = event.rsvp_user_ids?.includes(user?.user_id);
  const isPast = new Date(event.scheduled_at) < new Date();
  const cat = CATEGORIES[event.category] || CATEGORIES.general;
  const eventDate = new Date(event.scheduled_at);

  return (
    <div className="min-h-screen bg-background">
      <div className="h-48 md:h-64 relative">
        {event.cover_image_url ? (
          <img src={event.cover_image_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/40 via-purple-600/30 to-pink-600/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="absolute top-4 left-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/events')} className="bg-card/50 backdrop-blur" data-testid="event-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 -mt-16 relative z-10 pb-12">
        <Card className="bg-card border-border/50 p-6 md:p-8" data-testid="event-detail-card">
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className={cat.color}>{cat.label}</Badge>
            {event.status === 'cancelled' && <Badge variant="destructive">Cancelled</Badge>}
            {event.status === 'live' && <Badge className="bg-red-500 text-white animate-pulse">LIVE NOW</Badge>}
            {event.status === 'completed' && <Badge variant="secondary">Completed</Badge>}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {event.title}
          </h1>

          {event.description && <p className="text-muted-foreground mb-6">{event.description}</p>}

          <div className="bg-muted/30 rounded-xl p-4 mb-6 border border-border/30">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-sm font-semibold text-foreground">
                    {eventDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Time</p>
                  <p className="text-sm font-semibold text-foreground">
                    {eventDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
                  </p>
                </div>
              </div>
            </div>
            {!isPast && event.status === 'scheduled' && (
              <div className="mt-3 pt-3 border-t border-border/30 text-center">
                <p className="text-sm text-muted-foreground">Starts in</p>
                <p className="text-lg font-bold text-primary font-mono" data-testid="countdown">{countdown}</p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <UserAvatar src={event.host_avatar} alt={event.host_name} size="md" userId={event.host_user_id} userName={event.host_name} />
            <div>
              <p className="text-sm text-muted-foreground">Hosted by</p>
              <Link to={`/u/${event.host_handle || event.host_user_id}`} className="font-semibold text-foreground hover:text-primary transition-colors">
                {event.host_name}
              </Link>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-6">
            {!isHost && event.status === 'scheduled' && !isPast && (
              <Button onClick={handleRsvp} variant={isRsvpd ? 'secondary' : 'default'} className="flex-1 sm:flex-none" data-testid="rsvp-detail-btn">
                {isRsvpd ? <><BellOff className="w-4 h-4 mr-2" /> Cancel RSVP</> : <><Bell className="w-4 h-4 mr-2" /> Remind Me</>}
              </Button>
            )}
            {isHost && event.status === 'scheduled' && !isPast && (
              <Button onClick={() => navigate('/go-live')} className="flex-1 sm:flex-none" data-testid="go-live-btn">
                <Radio className="w-4 h-4 mr-2" /> Go Live Now
              </Button>
            )}
            <Button variant="outline" onClick={handleShare} data-testid="share-event-btn">
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
            {isHost && (
              <Button variant="outline" className="text-red-500 border-red-500/30 hover:bg-red-500/10" onClick={handleDelete} data-testid="cancel-event-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Cancel Event
              </Button>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" /> Attending ({event.rsvp_count || 0})
            </h3>
            {rsvps.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {rsvps.map(u => (
                  <Link key={u.user_id} to={`/u/${u.handle || u.user_id}`}
                    className="flex items-center gap-2 bg-muted/30 rounded-full pl-1 pr-3 py-1 hover:bg-muted/50 transition-colors">
                    <UserAvatar src={u.picture} alt={u.name} size="xs" userId={u.user_id} userName={u.name} />
                    <span className="text-xs text-foreground">{u.name}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No RSVPs yet. Be the first!</p>
            )}
          </div>

          {event.tags?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border/30 flex flex-wrap gap-2">
              {event.tags.map(tag => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
